import { Button, Form, Select, InputNumber, Input, message, notification } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import PageBreadcrumb from "../../components/breadcrumb";
import { useAddCashMutation } from "../../services/cash";
import { useGetBranchesQuery } from "../../services/branches";
import { useGetOpenEmployeesQuery } from "../../services/employee";
import { Home, CashHistoryRoute } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddCashFormValues {
  branchId:    string;
  collectedBy: string;
  amount:      number;
  remark?:     string;
}

const breadcrumbItems = [
  { label: <HomeOutlined />, to: Home },
  { label: "Add Cash" },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const AddCash = () => {
  const [form]   = Form.useForm<AddCashFormValues>();
  const navigate = useNavigate();

  const { data: branchesResp, isFetching: branchesLoading } = useGetBranchesQuery();
  const { data: employeesResp, isFetching: employeesLoading } = useGetOpenEmployeesQuery(undefined);
  const [addCash, { isLoading: submitting }] = useAddCashMutation();

  const branchList = Array.isArray(branchesResp)
    ? branchesResp
    : (Array.isArray((branchesResp as any)?.data) ? (branchesResp as any).data : []);
  const branchOptions = branchList.map((b: any) => ({
    value: b._id || b.id,
    label: b.name,
  }));

  // collectedBy expects the employee's linked User _id, not the Employee document id.
  const employeeOptions = ((employeesResp as any)?.data || []).map((emp: any) => ({
    value: emp.user?._id || emp._id,
    label: emp.user?.name || emp.name || 'Unknown',
  }));

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const onFinish = async (values: AddCashFormValues) => {
    try {
      await addCash({
        branchId:    values.branchId,
        amount:      values.amount,
        collectedBy: values.collectedBy,
        date:        dayjs().format('YYYY-MM-DD'),
        remark:      values.remark,
      }).unwrap();
      // message.success('Cash added successfully');
      form.resetFields();
      navigate(CashHistoryRoute);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <div className="add-cash-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Add Cash</h2>
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        className="add-cash-form"
      >
        <Form.Item name="branchId" label="Branch" rules={[{ required: true, message: 'Please select a branch' }]}>
          <Select
            placeholder="Select branch"
            loading={branchesLoading}
            showSearch
            optionFilterProp="label"
            options={branchOptions}
          />
        </Form.Item>

        <Form.Item name="collectedBy" label="Employee" rules={[{ required: true, message: 'Please select an employee' }]}>
          <Select
            placeholder="Select employee"
            loading={employeesLoading}
            showSearch
            optionFilterProp="label"
            options={employeeOptions}
          />
        </Form.Item>

        <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Please enter amount' }]}>
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            precision={0}
            prefix="₹"
            placeholder="Enter amount"
            onKeyDown={(e) => {
              if (!/[\d]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                e.preventDefault();
              }
            }}
          />
        </Form.Item>

        <Form.Item name="remark" label="Description" className="field-span-2">
          <Input.TextArea rows={3} placeholder="Enter description (optional)" />
        </Form.Item>

        <div className="footer-buttons">
          <Button type="default" onClick={() => navigate(CashHistoryRoute)}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" className="save-btn" loading={submitting}>
            {submitting ? 'Adding...' : 'Add Cash'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddCash;
